import {
  type Appointment,
  type ExtendedAppointment,
  optimisticUpdateAppointmentStatus,
  updateAppointments,
  updateDropInAppointments,
} from "@repo/api";
import {
  Button,
  type ButtonProps,
  type ButtonType,
  playIcon,
  stopIcon,
} from "@repo/skapa";
import { t } from "@repo/translation";
import { ButtonsContainer, useSettings, useToast } from "@repo/ui";
import { formatDateToApi } from "@repo/utils";
import { getAppointmentApiErrorMessage, trackAnalytics } from "@repo/utils";
import React, { useState } from "react";

import { useModals } from "../modals/hooks";
import { isOpenSlot } from "./utils";
import { isActiveStatus } from "./utils/isActiveStatus";

export const AppointmentStartEndButton = ({
  appointment,
  size,
  onClick,
  type,
  iconOnly,
  mutateSearchAppointments,
}: {
  appointment: Appointment;
  size?: ButtonProps["size"];
  onClick?: () => void;
  mutateSearchAppointments?: () => void;
  type?: ButtonType;
  iconOnly?: boolean;
}) => {
  const { pushModal } = useModals();
  const { updateToast } = useToast();
  const { market } = useSettings();
  const [loading, setLoading] = useState(false);

  const currentAppointment = appointment;

  const getNextStatus = (
    status: Appointment["appointmentStatus"]
  ): Appointment["appointmentStatus"] =>
    status === "STARTED" ? "COMPLETED" : "STARTED";

  const persistUpdate = async (
    nextStatus: Appointment["appointmentStatus"]
  ) => {
    const action = nextStatus === "STARTED" ? "start" : "end";
    const now = formatDateToApi(new Date());

    if (currentAppointment.type === "DROP_IN") {
      const dropInPayload = {
        ...currentAppointment,
        appointmentStatus: nextStatus,
        countryCode: market.toUpperCase(),
        ...(nextStatus === "STARTED"
          ? { actualTimeWindowStartTime: now }
          : { actualTimeWindowEndTime: now }),
      };
      await updateDropInAppointments(dropInPayload, mutateSearchAppointments);
    } else {
      await updateAppointments(action, currentAppointment);
    }
  };

  const handleAction = async () => {
    setLoading(true);
    const nextStatus = getNextStatus(currentAppointment.appointmentStatus);

    optimisticUpdateAppointmentStatus(
      appointment.id,
      nextStatus,
      appointment.opportunityId
    );

    try {
      await persistUpdate(nextStatus);
      onClick?.();
    } catch (err) {
      updateToast({
        id: appointment.id,
        title: getAppointmentApiErrorMessage(err),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const isStarted = currentAppointment.appointmentStatus === "STARTED";

    pushModal({
      type: "CONFIRM",
      title: isStarted
        ? t("action.end_appointment")
        : t("action.start_appointment"),
      message: isStarted
        ? t("prompt.are_you_sure_end_appointment")
        : t("prompt.are_you_sure_start_appointment"),
      onConfirm: handleAction,
    });
  };

  const isStarted = currentAppointment.appointmentStatus === "STARTED";

  return (
    <Button
      ssrIcon={isStarted ? stopIcon : playIcon}
      iconOnly={iconOnly}
      text={isStarted ? t("action.end") : t("action.start")}
      title={
        iconOnly ? (isStarted ? t("action.end") : t("action.start")) : undefined
      }
      size={size}
      type={type ?? "primary"}
      disabled={
        !isActiveStatus(currentAppointment.appointmentStatus) || loading
      }
      loading={loading}
      onClick={handleClick}
    />
  );
};

export const AppointmentActionButtons = ({
  appointment,
  showMoreActions,
  onClickMoreActions,
  onClickStartEnd,
  mutateSearchAppointments,
}: {
  appointment: ExtendedAppointment;
  showMoreActions: boolean;
  onClickMoreActions?: (...args: any[]) => any;
  onClickStartEnd?: () => void;
  mutateSearchAppointments?: () => void;
}) => {
  if (showMoreActions) {
    return (
      <ButtonsContainer justifyContent="start">
        <AppointmentStartEndButton
          appointment={appointment}
          size="small"
          onClick={onClickStartEnd}
          mutateSearchAppointments={mutateSearchAppointments}
        />
        <Button
          text={t("label.more_actions")}
          size="small"
          type="secondary"
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            e.stopPropagation();
            onClickMoreActions?.();
            trackAnalytics("more_actions");
          }}
        />
      </ButtonsContainer>
    );
  }

  if (
    isOpenSlot(
      appointment.reasonCode,
      appointment.orderNumber,
      appointment.type
    )
  ) {
    return (
      <Button
        text={t("action.open_in_boka")}
        disabled={!appointment.appointmentLink}
        type="primary"
        size="small"
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
          e.preventDefault();
          e.stopPropagation();
          window.open(appointment?.appointmentLink, "_blank");
          trackAnalytics("open_in_boka");
        }}
      />
    );
  }

  return (
    <AppointmentStartEndButton
      appointment={appointment}
      onClick={onClickStartEnd}
      mutateSearchAppointments={mutateSearchAppointments}
    />
  );
};
